import { ArrowDownRight, ArrowUpRight, type LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatDeltaPct, type MetricDelta } from "@colab/shared";

export function KpiTile({
  label,
  value,
  delta,
  hint,
  icon: Icon,
  showDelta = true,
  className,
}: {
  label: string;
  value: string;
  delta?: MetricDelta;
  hint?: string;
  icon?: LucideIcon;
  showDelta?: boolean;
  className?: string;
}) {
  const renderDelta = showDelta && delta && delta.previous !== delta.current;
  const tone =
    delta?.direction === "up"
      ? "text-status-active"
      : delta?.direction === "down"
        ? "text-status-lost"
        : "text-muted-foreground";
  const DeltaIcon =
    delta?.direction === "up"
      ? ArrowUpRight
      : delta?.direction === "down"
        ? ArrowDownRight
        : null;

  return (
    <div
      className={cn(
        "group relative overflow-hidden rounded-xl border bg-card p-5 transition hover:border-foreground/15",
        className,
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
          {label}
        </span>
        {Icon && (
          <Icon className="h-4 w-4 text-muted-foreground/60 transition group-hover:text-accent" />
        )}
      </div>
      <div className="mt-3 flex items-baseline gap-2">
        <span className="font-display text-3xl font-semibold tracking-tight tabular-nums">
          {value}
        </span>
        {renderDelta && delta && (
          <span
            className={cn(
              "inline-flex items-center gap-0.5 text-xs font-medium tabular-nums",
              tone,
            )}
            title={`Previous: ${delta.previous.toLocaleString()}`}
          >
            {DeltaIcon && <DeltaIcon className="h-3 w-3" />}
            {formatDeltaPct(delta)}
          </span>
        )}
      </div>
      {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}
